import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import initApollo from '~/jobiqo/apollo/initApollo';
import { getOrganizationProfileByPathQuery } from '~/jobiqo/components/Organization/queries/getOragizationQuery';
import { getAppConfig } from '~/jobiqo/lib/config';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
import { isPermanentRedirectCode, isRedirectCode } from '~/jobiqo/lib/redirect';
import { getTranslatedLink } from '~/jobiqo/utils/links';
import { getCleanUrlPart } from '~/jobiqo/components/Search/utils/searchHelpers';
import { getOrganizationPagePaths } from './paths';

export const getOrganizationPageRequestContext = async (
  ctx: GetServerSidePropsContext,
) => {
  const backendConfig = await getAppConfig(ctx);
  const frontendConfig = getServerFrontendConfig(ctx);

  // Because organizations are nodes we need to send Drupal the actual URL (translated).
  // ctx.resolvedUrl is the original URL.
  const fullPath = getCleanUrlPart(
    getTranslatedLink(ctx.resolvedUrl, ctx.locale),
  );
  const paths = getOrganizationPagePaths(fullPath);
  const apolloClient = initApollo({}, ctx);

  return {
    apolloClient,
    backendConfig,
    frontendConfig,
    ...paths,
  };
};

export const getOrganizationProfileResult = ({
  apolloClient,
  organizationPath,
  language,
  minisiteEnabled,
  blogEnabled,
}: {
  apolloClient: any;
  organizationPath: string;
  language?: string;
  minisiteEnabled: any;
  blogEnabled?: boolean;
}) => {
  return apolloClient.query({
    query: getOrganizationProfileByPathQuery(minisiteEnabled, blogEnabled),
    variables: {
      path: organizationPath,
      language,
    },
  });
};

export const getOrganizationRouteResponse = ({
  data,
  error,
  organizationPath,
  subPath,
}: {
  data: any;
  error?: any;
  organizationPath: string;
  subPath?: string;
}): GetServerSidePropsResult<Record<string, unknown>> | null => {
  if (error) {
    return {
      props: {
        error: true,
      },
    };
  }

  if (data?.route?.code === 403) {
    return {
      props: {},
      redirect: {
        destination: '/403',
        permanent: false,
      },
    };
  }

  if (
    organizationPath !== '/' &&
    data?.organizationProfileByPath?.code === 404
  ) {
    return {
      notFound: true,
    };
  }

  if (
    isRedirectCode(data?.route?.code) &&
    organizationPath !== data?.route?.target
  ) {
    const destination = subPath
      ? `${data.route.target.replace(/\/$/, '')}/${subPath}`
      : data.route.target;

    // If we get a redirect response redirect to the target.
    return {
      redirect: {
        destination,
        permanent: isPermanentRedirectCode(data?.route?.code),
      },
      props: {},
    };
  }

  return null;
};
